import { apiClient } from './apiClient';

export enum ReportReason {
  SPAM = 'SPAM',
  HARASSMENT = 'HARASSMENT',
  HATE_SPEECH = 'HATE_SPEECH',
  VIOLENCE = 'VIOLENCE',
  NUDITY = 'NUDITY',
  FALSE_INFORMATION = 'FALSE_INFORMATION',
  INTELLECTUAL_PROPERTY = 'INTELLECTUAL_PROPERTY',
  IMPERSONATION = 'IMPERSONATION',
  SELF_HARM = 'SELF_HARM',
  ILLEGAL_CONTENT = 'ILLEGAL_CONTENT',
  OTHER = 'OTHER'
}

export enum ReportStatus {
  PENDING = 'PENDING',
  REVIEWING = 'REVIEWING',
  RESOLVED = 'RESOLVED',
  REJECTED = 'REJECTED',
  DISMISSED = 'DISMISSED'
}

export interface CreateReportRequest {
  targetType: 'POST' | 'COMMENT' | 'USER';
  targetId: number;
  reason: ReportReason;
  description: string;
}

export interface UserReport {
  id: number;
  reporterId: number;
  reporterName: string;
  reporterUsername?: string; // Optional for backward compatibility
  reporterAvatarUrl?: string;
  targetType: 'POST' | 'COMMENT' | 'USER';
  targetId: number;
  reason: ReportReason | string; // Can be string from API
  description: string;
  status: ReportStatus | string; // Can be string from API
  resolution?: string | null;
  resolvedAt?: string | null;
  resolvedById?: number | null;
  resolvedByName?: string | null;
  reviewedBy?: number; // Legacy field
  reviewedAt?: string; // Legacy field
  adminNote?: string; // Legacy field
  createdAt: string;
  updatedAt?: string;
  // Reported content info (may not be in API response)
  targetContent?: string; // Content of the reported post/comment
  targetAuthorId?: number; // ID of user who created the content
  targetAuthorUsername?: string; // Username of violator
  targetAuthorDisplayName?: string; // Display name of violator
  targetAuthorAvatarUrl?: string; // Avatar of violator
}

export interface ReportResponse {
  action: string;
  success: boolean;
  data: UserReport;
}

export interface ReportPageResponse<T> {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
}

class ReportService {
  /**
   * User reports a post
   */
  async reportPost(request: CreateReportRequest): Promise<ReportResponse> {
    return await apiClient.post<ReportResponse>('/api/social/report/post', request);
  }

  /**
   * User gets their own reports
   */
  async getMyReports(page = 0, size = 20): Promise<ReportPageResponse<UserReport>> {
    const response: any = await apiClient.get('/api/social/reports', {
      params: { page, size }
    });
    return response.data;
  }

  /**
   * Admin gets all reports (filtered by status)
   */
  async getReports(page = 0, size = 20, status?: ReportStatus): Promise<ReportPageResponse<UserReport>> {
    const response: any = await apiClient.get('/api/admin/reports', {
      params: { page, size, status }
    });
    return response.data;
  }

  /**
   * Admin gets report by ID
   */
  async getReportById(reportId: number): Promise<UserReport> {
    const response: any = await apiClient.get(`/api/admin/reports/${reportId}`);
    return response.data;
  }

  /**
   * Admin resolves a report
   */
  async resolveReport(reportId: number, resolution: string): Promise<UserReport> {
    return await apiClient.post<UserReport>(`/api/admin/reports/${reportId}/resolve`, null, {
      params: { resolution }
    });
  }

  /**
   * Admin rejects a report
   */
  async rejectReport(reportId: number, reason: string): Promise<UserReport> {
    return await apiClient.post<UserReport>(`/api/admin/reports/${reportId}/reject`, null, {
      params: { reason }
    });
  }

  /**
   * Get localized reason text
   */
  getReasonText(reason: ReportReason | string): string {
    const reasonStr = reason.toString().toUpperCase();
    const reasonMap: Record<string, string> = {
      'SPAM': 'Spam hoặc quảng cáo',
      'HARASSMENT': 'Quấy rối hoặc bắt nạt',
      'HATE_SPEECH': 'Ngôn từ gây thù ghét',
      'VIOLENCE': 'Bạo lực hoặc đe dọa',
      'NUDITY': 'Nội dung khiêu dâm',
      'FALSE_INFORMATION': 'Thông tin sai lệch',
      'INTELLECTUAL_PROPERTY': 'Vi phạm bản quyền',
      'IMPERSONATION': 'Giả mạo danh tính',
      'SELF_HARM': 'Tự gây hại bản thân',
      'ILLEGAL_CONTENT': 'Nội dung bất hợp pháp',
      'OTHER': 'Lý do khác'
    };
    return reasonMap[reasonStr] || reason.toString();
  }

  /**
   * Get localized status text
   */
  getStatusText(status: ReportStatus): string {
    const statusMap: Record<ReportStatus, string> = {
      [ReportStatus.PENDING]: 'Đang chờ xử lý',
      [ReportStatus.REVIEWING]: 'Đang xem xét',
      [ReportStatus.RESOLVED]: 'Đã xử lý',
      [ReportStatus.REJECTED]: 'Đã từ chối',
      [ReportStatus.DISMISSED]: 'Đã bỏ qua'
    };
    return statusMap[status] || status;
  }

  /**
   * Get status color class
   */
  getStatusColor(status: ReportStatus): string {
    const colorMap: Record<ReportStatus, string> = {
      [ReportStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [ReportStatus.REVIEWING]: 'bg-blue-100 text-blue-800',
      [ReportStatus.RESOLVED]: 'bg-green-100 text-green-800',
      [ReportStatus.REJECTED]: 'bg-red-100 text-red-800',
      [ReportStatus.DISMISSED]: 'bg-gray-100 text-gray-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  }
}

export const reportService = new ReportService();
